import { FC, useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLoaderData, useNavigate, useParams, LoaderFunctionArgs } from 'react-router-dom';
import styled from 'styled-components';
import type { RootState, User } from '../types';

import MainContainer from '../components/ui_components/MainContainer';
import { LoadingState } from '../components/stats_components/LoadingState';

import {
    WrestlerBasicInfo,
    WrestlerStats,
    WrestlerAvatar,
    WrestlerLastMatch,
    WrestlerActions,
    WrestlerUserInfo,
    WrestlerBestYears,
    WrestlerBestOpponents,
    WrestlerBestRandomMatches,
    WrestlerAllMatches,
    WrestlerCards,
} from '../components/wrestler_page_components';
import { apiGetWrestlerCards } from '../api/cardApi';
import type { Card } from '../types/models/card';


interface WrestlerData {
    id: number;
    name: string;
    forum_url?: string | null;
    user?: { id: number; username: string; nickname_forum?: string } | null;
    user_id?: number | null;
    last_fight: Array<{
        ppv: {
            id: number;
            ppv_description: {
                name: string;
            };
        };
    }>;
    bestFights: unknown[];
    mostChallenged: unknown[];
    bestYears: unknown[];
    allFights: unknown[];
}

// ─── Layout ───────────────────────────────────────────────────────────────────

const PageWrapper = styled.div`
    width: 100%;
    max-width: 1100px;
    margin: 0 auto;
    padding: 40px 24px 16px;
    color: white;
`;

const LegacySectionsWrapper = styled.div`
    overflow: auto;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: white;
    padding-bottom: 80px;
`;

const HeroSection = styled.div`
    display: flex;
    gap: 0;
    align-items: stretch;
    margin-bottom: 32px;
    background-color: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 12px;
    overflow: hidden;

    @media (max-width: 768px) {
        flex-direction: column;
    }
`;

const AvatarPanel = styled.div`
    width: 200px;
    flex-shrink: 0;
    background-color: rgba(163, 27, 27, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    border-right: 1px solid rgba(255, 255, 255, 0.06);

    @media (max-width: 768px) {
        width: 100%;
        border-right: none;
        border-bottom: 1px solid rgba(255, 255, 255, 0.06);
        padding: 20px;
        justify-content: center;
    }
`;

const InfoPanel = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
    padding: 24px 28px;
`;

const WrestlerNameStyled = styled.h1`
    font-size: 36px;
    font-weight: 900;
    color: #fff;
    text-transform: uppercase;
    margin: 0 0 8px;
    letter-spacing: 0.02em;
    line-height: 1.1;
`;

const ForumButton = styled.button`
    align-self: flex-start;
    background-color: transparent;
    color: rgba(255, 255, 255, 0.6);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 5px;
    padding: 5px 14px;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-top: 4px;

    &:hover {
        border-color: rgba(255, 255, 255, 0.5);
        color: #fff;
    }
`;

const Divider = styled.div`
    width: 100%;
    height: 1px;
    background-color: rgba(255, 255, 255, 0.07);
    margin: 8px 0;
`;

const BeltsWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 4px;

    & > div {
        display: flex;
        align-items: center;
        gap: 6px;
        background-color: rgba(255, 255, 255, 0.04);
        border: 1px solid rgba(255, 255, 255, 0.08);
        border-radius: 6px;
        padding: 5px 10px;
        font-size: 13px;
        color: rgba(255, 255, 255, 0.75);

        & img {
            width: 22px;
            height: 22px;
            object-fit: contain;
        }
    }
`;

// ─── Sezione dettagli (info base + ultimo match) ───────────────────────────────

const DetailSection = styled.div`
    display: grid;
    grid-template-columns: 1fr 300px;
    gap: 20px;
    margin-bottom: 16px;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }
`;

const InfoCard = styled.div`
    background-color: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.07);
    border-radius: 10px;
    padding: 20px 24px;
`;

const InfoCardTitle = styled.div`
    font-size: 10px;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.3);
    text-transform: uppercase;
    letter-spacing: 0.1em;
    margin-bottom: 16px;
`;

// ─── Component ────────────────────────────────────────────────────────────────

const Wrestler: FC = () => {
    const { user } = useSelector((state: RootState) => state.auth);
    const initialData = useLoaderData() as WrestlerData | null;
    const [wrestlerData, setWrestlerData] = useState<WrestlerData | null>(initialData);
    const [isLoading, setIsLoading] = useState(false);
    const [wrestlerCards, setWrestlerCards] = useState<Card[]>([]);
    const [isLoadingCards, setIsLoadingCards] = useState(true);
    const navigate = useNavigate();
    const params = useParams<{ idString: string }>();
    const isInitialMount = useRef(true);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        const controller = new AbortController();

        const loadWrestlerData = async (): Promise<void> => {
            window.scrollTo(0, 0);
            setIsLoading(true);

            try {
                const response = await fetch(process.env.REACT_APP_BACKEND_URL + 'api/wrestlers/' + params.idString, { signal: controller.signal });
                if (response.ok) {
                    const data = await response.json();
                    if (!controller.signal.aborted && data && typeof data === 'object' && 'id' in data) {
                        setWrestlerData(data);
                    }
                }
            } catch (error) {
                if (error instanceof Error && error.name !== 'AbortError') {
                    console.error('Errore nel caricamento dei dati del wrestler:', error);
                }
            } finally {
                if (!controller.signal.aborted) setIsLoading(false);
            }
        };

        loadWrestlerData();
        return () => controller.abort();
    }, [params.idString]);

    useEffect(() => {
        if (!wrestlerData?.id) return;
        const controller = new AbortController();
        setIsLoadingCards(true);
        apiGetWrestlerCards(wrestlerData.id)
            .then(res => { if (!controller.signal.aborted) setWrestlerCards(res.data ?? []); })
            .catch(() => { if (!controller.signal.aborted) setWrestlerCards([]); })
            .finally(() => { if (!controller.signal.aborted) setIsLoadingCards(false); });
        return () => controller.abort();
    }, [wrestlerData?.id]);

    const refreshWrestlerData = async (): Promise<void> => {
        try {
            const response = await fetch(process.env.REACT_APP_BACKEND_URL + 'api/wrestlers/' + params.idString);
            if (response.ok) {
                const data = await response.json();
                if (data && typeof data === 'object' && 'id' in data) {
                    setWrestlerData(data);
                }
            }
        } catch (error) {
            console.error('Errore nel refresh dei dati del wrestler:', error);
        }
    };

    const navigateToPpv = (): void => {
        if (!wrestlerData?.last_fight?.length) return;
        const ppvId = wrestlerData.last_fight[0].ppv.id;
        const ppvName = wrestlerData.last_fight[0].ppv.ppv_description.name;
        navigate(`/ppv/specific/i_${ppvId}_d_${ppvName}`);
    };

    if (isLoading) {
        return <LoadingState />;
    }

    if (wrestlerData == null || !('id' in wrestlerData)) {
        return (
            <MainContainer className="main-container-wrestler">
                <PageWrapper style={{ textAlign: 'center', paddingTop: '80px' }}>
                    Nessun dato trovato per questo wrestler.
                </PageWrapper>
            </MainContainer>
        );
    }

    return (
        <MainContainer className="main-container-wrestler">
            <PageWrapper>

                {/* ── Hero ── */}
                <HeroSection>
                    <AvatarPanel>
                        <WrestlerAvatar wrestlerData={wrestlerData} />
                    </AvatarPanel>

                    <InfoPanel>
                        <WrestlerNameStyled>{wrestlerData.name.toUpperCase()}</WrestlerNameStyled>

                        <WrestlerUserInfo wrestlerData={wrestlerData} />

                        <WrestlerActions
                            user={user as User}
                            wrestlerData={wrestlerData as any}
                            onWrestlerDataUpdate={refreshWrestlerData}
                        />

                        {wrestlerData.forum_url && (
                            <ForumButton onClick={() => window.open(wrestlerData.forum_url!, '_blank')}>
                                Profilo Forum
                            </ForumButton>
                        )}

                        <Divider />

                        <WrestlerStats wrestlerData={wrestlerData} />

                        {(wrestlerData as any).belts?.length > 0 && (
                            <>
                                <Divider />
                                <BeltsWrapper>
                                    {(wrestlerData as any).belts.map((belt: any, i: number) => (
                                        <div key={i}>
                                            <img src={process.env.REACT_APP_STORAGE_URL + belt.img_path} alt={belt.name} />
                                            <span>{belt.belt_count}x {belt.name}</span>
                                        </div>
                                    ))}
                                </BeltsWrapper>
                            </>
                        )}
                    </InfoPanel>
                </HeroSection>

                {/* ── Dettagli + Ultimo Match ── */}
                <DetailSection>
                    <InfoCard>
                        <InfoCardTitle>Scheda tecnica</InfoCardTitle>
                        <WrestlerBasicInfo wrestlerData={wrestlerData} />
                    </InfoCard>

                    {wrestlerData.last_fight?.length > 0 && (
                        <InfoCard style={{ padding: '16px' }}>
                            <WrestlerLastMatch
                                wrestlerData={wrestlerData}
                                onNavigateToPpv={navigateToPpv}
                            />
                        </InfoCard>
                    )}
                </DetailSection>

            </PageWrapper>

            {/* ── Carte associate ── */}
            {(isLoadingCards || wrestlerCards.length > 0) && (
                <WrestlerCards
                    cards={wrestlerCards}
                    isLoading={isLoadingCards}
                    isAuthenticated={!!user}
                />
            )}

            {/* ── Sezioni esistenti — larghezza originale ── */}
            <LegacySectionsWrapper>
                {wrestlerData.bestFights.length > 0 && (
                    <WrestlerBestRandomMatches wrestlerData={wrestlerData} />
                )}
                {wrestlerData.mostChallenged.length > 0 && (
                    <WrestlerBestOpponents wrestlerData={wrestlerData} />
                )}
                {wrestlerData.bestYears.length > 0 && (
                    <WrestlerBestYears wrestlerData={wrestlerData} />
                )}
                {wrestlerData.allFights.length > 0 && (
                    <WrestlerAllMatches wrestlerData={wrestlerData} />
                )}
            </LegacySectionsWrapper>
        </MainContainer>
    );
};

export default Wrestler;

export async function loader({ params }: LoaderFunctionArgs) {
    const idString = params.idString;
    if (!idString) return null;

    const response = await fetch(process.env.REACT_APP_BACKEND_URL + 'api/wrestlers/' + idString);
    if (!response.ok) {
        return null;
    } else {
        const resData = await response.json();
        return resData;
    }
}
